import React from 'react';

/**
 * BrandEmblem - The official WriteRight PCM+ trademarked emblem
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 * BRAND SYMBOLISM & INNUENDOS
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * THE LOGO: Stethoscope Wrapped Around Hourglass
 * ─────────────────────────────────────────────────
 * • STETHOSCOPE: The universal symbol of clinical care, listening, diagnosis
 * • HOURGLASS: Time - the metric that haunts every provider
 * • WRAPPED TOGETHER: Healthcare constrained by time, but also...
 *   time reclaimed through better tools. The stethoscope embraces time
 *   rather than fighting it.
 * 
 * "WriteRight" - THE NAME
 * ─────────────────────────────────────────────────
 * • WRITERIGHT: Compose documentation correctly, accurately, completely
 * • RIGHT WRITE: The correct way to document - efficient, thorough, fast
 * • RITE: A ritual, a practice - charting as ceremony, not burden
 * • Double meaning: "Write" (compose) + "Right" (correct/proper)
 * 
 * "PCM+" - THE DESIGNATION
 * ─────────────────────────────────────────────────
 * • PCM: Primary Care Manager - the military term for your assigned provider
 * • PCM+: Your PCM, enhanced. The PLUS is the multiplier effect.
 * • Hidden meaning: "The RIGHT PCM for the job" - this tool IS your PCM+
 * • Plus (+) represents: addition, positivity, more capability
 * 
 * "People First" - THE TAGLINE
 * ─────────────────────────────────────────────────
 * • Military slogan repurposed: People First, Mission Always
 * • Here: People First, Paperwork Second
 * • The patient comes first, but the documentation enables their care
 * • The PROVIDER is also "people" - their wellbeing matters too
 * 
 * GOLD GRADIENT (PCM+)
 * ─────────────────────────────────────────────────
 * • Gold: Excellence, premium quality, the "gold standard" of care
 * • Gradient: Depth, dimension, not flat/one-dimensional
 * • Military connection: Gold oak leaves, achievement, rank
 * 
 * THE TIME MOTIF
 * ─────────────────────────────────────────────────
 * • Time is always "in play" as the metric of success
 * • Providers are measured by RVUs, patient volume, throughput
 * • Yet quality requires TIME - the eternal tension
 * • This tool resolves the paradox: fast AND thorough
 * 
 * MOTTO: "Because the system won't change — but your workflow can."
 * ─────────────────────────────────────────────────
 * • Acknowledges the broken system without dwelling
 * • Empowers individual agency within constraints
 * • Pragmatic, not idealistic
 * • The em-dash creates a pause, a pivot, a BUT
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * This component contains the exact positioning, sizing, and styling
 * of the brand emblem. Use this everywhere the brand appears.
 * 
 * The background changes with themes - but this emblem stays consistent.
 * 
 * Sizes:
 * - 'lg' (large): For SiteGate, RoleSelector, Footer - full size
 * - 'md' (medium): For SessionViewer headers
 * - 'sm' (small): For main app Header
 */

export const BrandEmblem = ({ size = 'lg', className = '' }) => {
  // Size configurations matching the finalized design
  const sizes = {
    lg: {
      logo: { width: 180, height: 120, left: -20, opacity: 0.8 },
      container: { marginLeft: 100, marginTop: 28 },
      title: { fontSize: 36 },
      subtitle: { fontSize: 36, paddingLeft: 10 },
      tagline: { fontSize: 16, paddingLeft: 10 }
    },
    md: {
      logo: { width: 110, height: 75, left: -10, opacity: 0.8 },
      container: { marginLeft: 62, marginTop: 16 },
      title: { fontSize: 24 },
      subtitle: { fontSize: 24, paddingLeft: 6 },
      tagline: { fontSize: 12, paddingLeft: 6 }
    },
    sm: {
      logo: { width: 60, height: 40, left: -4, opacity: 0.7 },
      container: { marginLeft: 38, marginTop: 8 },
      title: { fontSize: 14 },
      subtitle: { fontSize: 14, paddingLeft: 3 },
      tagline: { fontSize: 8, paddingLeft: 3 }
    }
  };

  const s = sizes[size] || sizes.lg;

  return (
    <div className={`brand-emblem ${className}`} style={{ display: 'flex', alignItems: 'center', position: 'relative', pointerEvents: 'none' }}>
      {/* Hourglass Logo - Stethoscope wrapped around time */}
      {/* Time is always in play as the metric of success */}
      <img 
        src="/emblem_icon.png" 
        alt="WriteRight PCM+ Logo - Stethoscope embracing time" 
        style={{ 
          width: s.logo.width,
          height: s.logo.height,
          objectFit: 'contain',
          position: 'absolute',
          left: s.logo.left,
          opacity: s.logo.opacity,
          zIndex: 0,
          pointerEvents: 'none'
        }}
      />
      
      {/* Text Block - positioned to the right */}
      <div style={{ 
        position: 'relative',
        zIndex: 1,
        marginLeft: s.container.marginLeft,
        marginTop: s.container.marginTop,
        textAlign: 'left'
      }}>
        {/* WriteRight - Right Write / Rite */}
        <div style={{ 
          fontSize: s.title.fontSize,
          fontWeight: 'bold',
          color: 'var(--brand-text-color, white)',
          lineHeight: 1.1,
          marginBottom: 0
        }}>
          WriteRight
        </div>
        
        {/* PCM+ with gold gradient - The RIGHT PCM for the job */}
        <div style={{ 
          fontSize: s.subtitle.fontSize,
          fontWeight: 'bold',
          lineHeight: 1.1,
          marginBottom: 4,
          paddingLeft: s.subtitle.paddingLeft,
          background: 'linear-gradient(180deg, #FFD700 0%, #DAA520 50%, #B8860B 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          PCM+
        </div>
        
        {/* People First tagline - People First, Paperwork Second */}
        <div style={{ 
          fontSize: s.tagline.fontSize,
          fontWeight: 'bold',
          color: 'var(--brand-text-color, white)',
          paddingLeft: s.tagline.paddingLeft
        }}>
          People First
        </div>
      </div>
    </div>
  );
};

/**
 * BrandMotto - The tagline that appears below the emblem
 * 
 * "Because the system won't change — but your workflow can."
 * 
 * The em-dash creates a rhetorical pivot: acknowledgment → empowerment
 */
export const BrandMotto = ({ className = '' }) => (
  <p className={className} style={{ 
    color: 'var(--brand-motto-color, #64748b)',
    fontSize: 12,
    textAlign: 'center',
    marginTop: 24,
    marginBottom: 0
  }}>
    Because the system won't change — but your workflow can.
    <br />
    Built from burnout. Built to last.
  </p>
);

/**
 * Brand Symbolism Export - For documentation and marketing
 */
export const BRAND_SYMBOLISM = {
  logo: {
    element: 'Stethoscope wrapped around hourglass',
    meaning: 'Healthcare constrained by time, yet time reclaimed through better tools',
    innuendo: 'The stethoscope embraces time rather than fighting it'
  },
  name: {
    primary: 'WriteRight',
    meanings: [
      'WriteRight: Compose documentation correctly',
      'Right Write: The correct way to document',
      'Rite: Charting as ceremony, not burden'
    ]
  },
  designation: {
    primary: 'PCM+',
    meanings: [
      'Primary Care Manager - enhanced',
      'The RIGHT PCM for the job',
      'Plus (+): Addition, positivity, multiplier effect'
    ]
  },
  tagline: {
    primary: 'People First',
    subtext: 'Paperwork Second',
    origin: 'Military slogan repurposed: People First, Mission Always'
  },
  motto: {
    text: 'Because the system won\'t change — but your workflow can.',
    rhetoric: 'Acknowledgment → Empowerment pivot via em-dash'
  },
  colors: {
    gold: 'Excellence, premium quality, military achievement',
    gradient: 'Depth, dimension, not one-dimensional'
  },
  time: {
    motif: 'Time is always in play as the metric of success',
    tension: 'RVUs vs quality - the eternal provider paradox',
    resolution: 'Fast AND thorough - no longer mutually exclusive'
  }
};

export default BrandEmblem;
